import { observer } from "mobx-react";
import React, { MouseEvent, useEffect, useState } from "react";

import { useMainStore } from "@/contexts/Store";

import Loading from "../Loading";
import ListSettings from "../table/settings/ListSettings";
import ListTitleSettings from "../table/settings/ListTitleSettings";
import Table from "../table/Table";
import CompanyTagsNavigation from "./CompanyTagsNavigation";

export type TagModel = "Tag" | "System" | "Department" | "Product";
export type TagItem = "tags" | "systems" | "departments" | "products";

const headers = [
  {
    field_name: "title",
    title: "Title",
    width: 100,
  },
];

type Props = {
  items: TagItem;
  model: TagModel;
};

function CompanyTags({ items, model }: Props) {
  // Import MobX stores
  const mainStore = useMainStore();

  // State
  const [newRecord, setNewRecord] = useState<object | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const { company } = mainStore.companies;
  let fields;
  switch (items) {
    case "tags":
      fields = mainStore.tags.tags;
      break;
    case "departments":
      fields = mainStore.departments.departments;
      break;
    case "systems":
      fields = mainStore.systems.systems;
      break;
    case "products":
      fields = mainStore.products.products;
      break;
    default:
      throw new Error(`unexpected case ${items}`);
  }

  useEffect(() => {
    (async () => {
      if (company?.id) {
        await mainStore[items].index(company.id);
        setLoading(false);
      }
    })();
  }, [company]);

  function addNewItem(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    if (!newRecord) {
      setNewRecord({});
    }
  }

  function removeNewItem() {
    setNewRecord(null);
  }

  return (
    <Table>
      <div className="settings-wrap">
        <CompanyTagsNavigation />
        <div className="settings-content-wrap">
          <div className="settings-explainer">
            What are tags? <br />
            Tags can be used to categorize your records. They are company
            specific and shared across all of a company&apos;s internal and
            collaborative workspaces.
          </div>
          <div className="list-title-table">
            <ul>
              <div className="list-title-table-wrap">
                {headers.map((header) => (
                  <ListTitleSettings
                    key={header.field_name}
                    width={header.width}
                    title={header.title}
                  />
                ))}
              </div>
              <div className="list-title-table-block" />
            </ul>
          </div>
          <div className="add-new-user">
            <button onClick={addNewItem}>Add New {model} +</button>
          </div>
          {newRecord && (
            <ListSettings
              key="new_record"
              headers={headers}
              data={newRecord}
              model={model}
              isNewRecord
              removeNewEntry={removeNewItem}
            />
          )}
          {loading && (
            <Loading loadingLayout="small-table" showTableHeader={false} />
          )}
          {!loading &&
            fields.map((field) => (
              <ListSettings
                inTag
                key={field.id}
                headers={headers}
                data={field}
                model={model}
              />
            ))}
        </div>
      </div>
    </Table>
  );
}

export default observer(CompanyTags);
